// HVNMDE — Motion bootstrap
// Lenis smooth scroll + GSAP ScrollTrigger reveals + cart-drawer toggle.
// Loaded as <script type="module"> from theme.liquid.

(function () {
  'use strict';

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // — Lenis smooth scroll
  function initLenis() {
    if (reduced || !window.Lenis) return null;
    const lenis = new window.Lenis({
      duration: 1.05,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      wheelMultiplier: 1,
      touchMultiplier: 1.6,
    });
    if (window.gsap && window.ScrollTrigger) {
      // single ticker owner: GSAP drives Lenis, Lenis updates ScrollTrigger —
      // prevents double-smoothing and pin jitter. Never normalizeScroll with Lenis.
      window.gsap.registerPlugin(window.ScrollTrigger);
      lenis.on('scroll', window.ScrollTrigger.update);
      window.gsap.ticker.add((t) => lenis.raf(t * 1000));
      window.gsap.ticker.lagSmoothing(0);
    } else {
      function raf(time) {
        lenis.raf(time);
        requestAnimationFrame(raf);
      }
      requestAnimationFrame(raf);
    }
    window.lenis = lenis;
    return lenis;
  }

  // — Split headlines into per-line reveal wrappers (janissne pattern)
  // Must run before initReveals so .is-in lands on already-split markup.
  function initSplitLines() {
    if (reduced) return;
    const els = document.querySelectorAll('[data-split]');
    if (!els.length) return;
    els.forEach((el) => { el.dataset.splitSrc = el.innerHTML; });
    function splitOne(el) {
      el.innerHTML = el.dataset.splitSrc;
      const brSplit = el.innerHTML.split(/<br\s*\/?>/i);
      let lines;
      if (brSplit.length > 1) {
        lines = brSplit.map((s) => s.replace(/<[^>]*>/g, '').trim()).filter(Boolean);
      } else {
        // word-wrap measurement: group words by rendered line
        const words = el.textContent.split(/\s+/).filter(Boolean);
        if (!words.length) return;
        el.innerHTML = words.map((w) => `<span class="split-word">${w}</span>`).join(' ');
        const spans = el.querySelectorAll('.split-word');
        lines = [];
        let lastTop = null;
        spans.forEach((s) => {
          if (s.offsetTop !== lastTop) { lines.push([]); lastTop = s.offsetTop; }
          lines[lines.length - 1].push(s.textContent);
        });
        lines = lines.map((ws) => ws.join(' '));
      }
      el.innerHTML = lines.map((line, i) =>
        `<span class="split-line"><span class="split-line__inner" style="--li:${i}">${line}</span></span>`
      ).join('');
      el.classList.add('is-split');
    }
    function splitAll() { els.forEach(splitOne); }
    splitAll();
    // line breaks shift once webfonts land or on resize — re-split so frozen lines never mismatch
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(splitAll);
    let rsT;
    window.addEventListener('resize', () => { clearTimeout(rsT); rsT = setTimeout(splitAll, 200); }, { passive: true });
  }

  // — Fit-finder: 2 taps → honest size recommendation (rule-based on the oversized cut)
  function initFitFinder() {
    const SIZES = ['XS', 'S', 'M', 'L', 'XL'];
    document.querySelectorAll('[data-fit-finder]').forEach((ff) => {
      let usual = null, pref = null;
      const result = ff.querySelector('[data-ff-result]');
      if (!result) return;
      function selectIn(group, btn) {
        ff.querySelectorAll('[data-ff-group="' + group + '"] .fit-finder__opt').forEach((b) => b.classList.remove('is-selected'));
        btn.classList.add('is-selected');
      }
      function update() {
        if (!usual || !pref) return;
        const i = SIZES.indexOf(usual);
        let rec, why;
        if (pref === 'true') {
          rec = usual; why = 'Your usual size lands the intended oversized, boxy fit.';
        } else if (i <= 0) {
          rec = 'XS'; why = 'XS is our smallest — expect a slightly relaxed fit rather than full volume.';
        } else {
          rec = SIZES[i - 1]; why = 'Sizing down one gives a cleaner fit with less volume.';
        }
        result.hidden = false;
        result.innerHTML = '<span class="fit-finder__rec">TAKE ' + rec + '</span><span class="fit-finder__why">' + why + '</span>';
      }
      ff.querySelectorAll('[data-ff-size]').forEach((b) => b.addEventListener('click', () => { usual = b.dataset.ffSize; selectIn('size', b); update(); }));
      ff.querySelectorAll('[data-ff-fit]').forEach((b) => b.addEventListener('click', () => { pref = b.dataset.ffFit; selectIn('fit', b); update(); }));
    });
  }

  // — Members preview accordion (smooth grid-rows expand, single-open)
  function initAccordion() {
    document.querySelectorAll('[data-accordion]').forEach((group) => {
      const rows = [...group.querySelectorAll('[data-acc]')];
      group.querySelectorAll('[data-acc-toggle]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const row = btn.closest('[data-acc]');
          const open = row.classList.contains('is-open');
          rows.forEach((r) => {
            r.classList.remove('is-open');
            r.querySelector('[data-acc-toggle]')?.setAttribute('aria-expanded', 'false');
          });
          if (!open) { row.classList.add('is-open'); btn.setAttribute('aria-expanded', 'true'); }
        });
      });
    });
  }

  // — Reveal on scroll (IntersectionObserver, GSAP optional)
  function initReveals() {
    const els = document.querySelectorAll('.reveal');
    if (!els.length) return;
    if (reduced) { els.forEach((el) => el.classList.add('is-in')); return; }
    // Above-the-fold hero content must never wait for the observer —
    // small titles can sit below the -10% trigger line and stay invisible.
    document.querySelectorAll('.hero .reveal').forEach((el) => el.classList.add('is-in'));
    // staggered delay per sibling group — capped so late items never feel laggy
    const groups = new Map();
    els.forEach((el) => {
      const key = el.parentElement;
      const i = groups.get(key) || 0;
      groups.set(key, i + 1);
      if (i > 0) el.style.transitionDelay = Math.min(i * 70, 420) + 'ms';
    });
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('is-in');
            io.unobserve(e.target);
          }
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.05 }
    );
    els.forEach((el) => io.observe(el));
  }

  // — Image loading skeleton: remove ::before once loaded
  function initImageLoader() {
    document.querySelectorAll('img').forEach((img) => {
      const wrap = img.closest('.card-product__media, .process-tile .media, .editorial-split__media, .pdp__media-stack .media');
      if (!wrap) return;
      if (img.complete && img.naturalHeight > 0) {
        wrap.classList.add('is-loaded');
      } else {
        img.addEventListener('load', () => wrap.classList.add('is-loaded'), { once: true });
        img.addEventListener('error', () => wrap.classList.add('is-loaded'), { once: true });
      }
    });
  }

  // — Cart drawer
  function initCartDrawer() {
    const drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer) return;
    const openers = document.querySelectorAll('[data-cart-open]');
    const closers = drawer.querySelectorAll('[data-cart-close]');
    const overlay = drawer.querySelector('[data-cart-overlay]');

    function open() {
      drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('lenis-stopped');
      if (window.lenis) window.lenis.stop();
      refreshCart();
    }
    function close() {
      drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('lenis-stopped');
      if (window.lenis) window.lenis.start();
    }

    openers.forEach((b) => b.addEventListener('click', (e) => { e.preventDefault(); open(); }));
    closers.forEach((b) => b.addEventListener('click', close));
    if (overlay) overlay.addEventListener('click', close);
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });

    // intercept add-to-cart forms
    document.addEventListener('submit', async (e) => {
      const form = e.target.closest('form[action="/cart/add"], form[action^="/cart/add"]');
      if (!form) return;
      e.preventDefault();
      const submitBtn = form.querySelector('[type="submit"]');
      const originalText = submitBtn ? submitBtn.textContent : '';
      if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = 'ADDING…'; }
      try {
        const fd = new FormData(form);
        const res = await fetch('/cart/add.js', { method: 'POST', body: fd, headers: { 'Accept': 'application/json' } });
        if (!res.ok) throw new Error('add failed');
        await refreshCart();
        open();
      } catch (err) {
        console.error('[cart]', err);
      } finally {
        if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = originalText; }
      }
    });

    // quick-add: collection card size pills → straight to bag, no PDP detour
    document.addEventListener('click', async (e) => {
      const btn = e.target.closest('[data-qa-variant]');
      if (!btn || btn.disabled) return;
      e.preventDefault();
      const id = Number(btn.getAttribute('data-qa-variant'));
      if (!id) return;
      const original = btn.textContent;
      btn.disabled = true; btn.textContent = '···';
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: id, quantity: 1 }),
        });
        if (!res.ok) throw new Error('add failed');
        await refreshCart();
        open();
      } catch (err) {
        console.error('[quickadd]', err);
      } finally {
        btn.disabled = false; btn.textContent = original;
      }
    });
  }

  async function refreshCart() {
    try {
      const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      const cart = await res.json();
      const drawer = document.querySelector('[data-cart-drawer]');
      const counter = document.querySelectorAll('[data-cart-count]');
      counter.forEach((c) => { c.textContent = String(cart.item_count); });
      if (!drawer) return;
      const list = drawer.querySelector('[data-cart-items]');
      const total = drawer.querySelector('[data-cart-total]');
      const empty = drawer.querySelector('[data-cart-empty]');
      if (cart.item_count === 0) {
        if (empty) empty.style.display = '';
        if (list) list.innerHTML = '';
      } else {
        if (empty) empty.style.display = 'none';
        if (list) list.innerHTML = cart.items.map(renderItem).join('');
      }
      if (total) total.textContent = formatMoney(cart.total_price);

      // free-shipping progress
      const progress = drawer.querySelector('[data-cart-progress]');
      if (progress) {
        const threshold = parseInt(drawer.dataset.freeShippingThreshold, 10) || 12000;
        const fill = progress.querySelector('[data-cart-progress-fill]');
        const labelEl = progress.querySelector('[data-cart-progress-label]');
        progress.hidden = cart.item_count === 0;
        const ratio = Math.min(1, cart.total_price / threshold);
        if (fill) fill.style.width = (ratio * 100).toFixed(1) + '%';
        if (labelEl) {
          if (ratio >= 1) {
            labelEl.textContent = 'FREE SHIPPING — UNLOCKED';
            labelEl.classList.add('is-unlocked');
          } else {
            labelEl.textContent = formatMoney(threshold - cart.total_price) + ' TO FREE SHIPPING';
            labelEl.classList.remove('is-unlocked');
          }
        }
      }

      // studio-credit preview (LIST baseline 5%) — makes the members value tangible at the money moment
      const creditEl = drawer.querySelector('[data-cart-credit]');
      if (creditEl) {
        const eur = Math.floor(cart.total_price * 0.05 / 100);
        if (cart.item_count > 0 && eur >= 1) {
          creditEl.hidden = false;
          creditEl.innerHTML = '<span class="dot"></span> EARN €' + eur + '+ STUDIO CREDIT ON THIS ORDER';
        } else {
          creditEl.hidden = true;
        }
      }

      // cross-sell: show first drop product not already in the bag
      const xsWrap = drawer.querySelector('[data-cross-sell-wrap]');
      if (xsWrap) {
        const inCart = new Set(cart.items.map((i) => i.handle));
        let shown = false;
        xsWrap.querySelectorAll('[data-cross-sell]').forEach((card) => {
          const show = !shown && cart.item_count > 0 && !inCart.has(card.dataset.crossSell);
          card.hidden = !show;
          if (show) shown = true;
        });
        xsWrap.hidden = !shown;
      }

      bindLineItemActions(drawer);
    } catch (e) {
      console.error('[cart refresh]', e);
    }
  }

  function renderItem(item) {
    const img = item.image ? `<img src="${item.image}" alt="${escapeHtml(item.product_title)}" loading="lazy">` : '';
    return `
      <li class="cart-line" data-line-key="${item.key}">
        <div class="cart-line__media media media-ratio-4-5">${img}</div>
        <div class="cart-line__info">
          <div class="spread">
            <span class="mono">${escapeHtml(item.product_title)}</span>
            <span class="mono">${formatMoney(item.final_line_price)}</span>
          </div>
          <div class="cart-line__meta mono muted">${escapeHtml(item.variant_title || '')}</div>
          <div class="cart-line__qty">
            <button type="button" data-cart-qty="-1" aria-label="decrease">−</button>
            <span class="mono">${item.quantity}</span>
            <button type="button" data-cart-qty="+1" aria-label="increase">+</button>
            <button type="button" class="mono cart-line__remove" data-cart-remove>REMOVE</button>
          </div>
        </div>
      </li>`;
  }

  function bindLineItemActions(drawer) {
    drawer.querySelectorAll('[data-cart-qty], [data-cart-remove]').forEach((btn) => {
      btn.addEventListener('click', async (e) => {
        const line = e.target.closest('[data-line-key]');
        if (!line) return;
        const key = line.dataset.lineKey;
        const isRemove = btn.hasAttribute('data-cart-remove');
        let qty;
        if (isRemove) qty = 0;
        else {
          const cur = parseInt(line.querySelector('.cart-line__qty span').textContent, 10);
          qty = cur + parseInt(btn.dataset.cartQty, 10);
        }
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: key, quantity: Math.max(0, qty) }),
        });
        await refreshCart();
      });
    });
  }

  function formatMoney(cents) {
    return new Intl.NumberFormat(document.documentElement.lang || 'de-DE', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'EUR',
    }).format(cents / 100);
  }
  function escapeHtml(s) { return String(s).replace(/[&<>"']/g, (c) => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c])); }

  // — Live clock for footer ticker (CET)
  function initClock() {
    const el = document.querySelector('[data-clock]');
    if (!el) return;
    function tick() {
      const now = new Date();
      const cet = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Berlin', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }).format(now);
      el.textContent = cet + ' CET';
    }
    tick();
    setInterval(tick, 1000);
  }

  // — Drop countdown
  function initCountdown() {
    document.querySelectorAll('[data-countdown]').forEach((el) => {
      const target = new Date(el.dataset.countdown).getTime();
      if (Number.isNaN(target)) return;
      function tick() {
        const ms = target - Date.now();
        if (ms <= 0) { el.textContent = 'LIVE NOW'; return; }
        const d = Math.floor(ms / 86400000);
        const h = Math.floor((ms % 86400000) / 3600000);
        const m = Math.floor((ms % 3600000) / 60000);
        const s = Math.floor((ms % 60000) / 1000);
        el.textContent = `T-${String(d).padStart(2,'0')}D ${String(h).padStart(2,'0')}H ${String(m).padStart(2,'0')}M ${String(s).padStart(2,'0')}S`;
      }
      tick();
      setInterval(tick, 1000);
    });
  }

  // — Generic slide-in drawer toggle (used by wishlist + mobile-menu)
  function bindDrawer(selector, openSel, closeSel, overlaySel, onOpen) {
    const drawer = document.querySelector(selector);
    if (!drawer) return null;
    const openers = document.querySelectorAll(openSel);
    const closers = drawer.querySelectorAll(closeSel);
    const overlay = drawer.querySelector(overlaySel);
    function open() {
      drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('lenis-stopped');
      if (window.lenis) window.lenis.stop();
      if (onOpen) onOpen();
    }
    function close() {
      drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('lenis-stopped');
      if (window.lenis) window.lenis.start();
    }
    openers.forEach((b) => b.addEventListener('click', (e) => { e.preventDefault(); open(); }));
    closers.forEach((b) => b.addEventListener('click', close));
    if (overlay) overlay.addEventListener('click', close);
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
    return { open, close, drawer };
  }

  // — Wishlist
  const WISHLIST_KEY = 'hvnmde:wishlist';
  function readWishlist() {
    try { return JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]'); } catch { return []; }
  }
  function writeWishlist(list) {
    localStorage.setItem(WISHLIST_KEY, JSON.stringify(list));
    document.dispatchEvent(new CustomEvent('hvnmde:wishlist-change', { detail: list }));
  }
  function renderWishlist() {
    const list = readWishlist();
    document.querySelectorAll('[data-wishlist-count]').forEach((el) => { el.textContent = String(list.length); });
    const drawer = document.querySelector('[data-wishlist-drawer]');
    if (drawer) {
      const items = drawer.querySelector('[data-wishlist-items]');
      const empty = drawer.querySelector('[data-wishlist-empty]');
      if (items) items.innerHTML = list.map(renderWishlistItem).join('');
      if (empty) empty.style.display = list.length === 0 ? '' : 'none';
      drawer.querySelectorAll('[data-wishlist-remove]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          const handle = e.currentTarget.dataset.wishlistRemove;
          writeWishlist(readWishlist().filter((x) => x.handle !== handle));
          renderWishlist();
          syncToggleStates();
        });
      });
    }
    // full-page wishlist
    const pageEmpty = document.querySelector('[data-wishlist-page-empty]');
    const pageGrid = document.querySelector('[data-wishlist-page-grid]');
    if (pageGrid) {
      pageGrid.style.display = list.length ? 'grid' : 'none';
      if (pageEmpty) pageEmpty.style.display = list.length ? 'none' : '';
      pageGrid.innerHTML = list.map(renderWishlistCard).join('');
      pageGrid.querySelectorAll('[data-wishlist-remove]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          const handle = e.currentTarget.dataset.wishlistRemove;
          writeWishlist(readWishlist().filter((x) => x.handle !== handle));
          renderWishlist();
          syncToggleStates();
        });
      });
    }
  }
  function renderWishlistItem(it) {
    return `<li class="cart-line" data-line-key="${it.handle}">
      <div class="cart-line__media media media-ratio-4-5"><img src="${it.image}" alt="${escapeHtml(it.title)}" loading="lazy"></div>
      <div class="cart-line__info">
        <div class="spread"><a href="/products/${it.handle}" class="mono">${escapeHtml(it.title)}</a><span class="mono">${formatMoney(it.price)}</span></div>
        <div class="cart-line__meta mono muted">${escapeHtml(it.code || '')}</div>
        <div class="cart-line__qty">
          <a href="/products/${it.handle}" class="mono" style="text-decoration: underline;">VIEW</a>
          <button type="button" class="mono cart-line__remove" data-wishlist-remove="${it.handle}">REMOVE</button>
        </div>
      </div>
    </li>`;
  }
  function renderWishlistCard(it) {
    return `<div class="card-product">
      <a href="/products/${it.handle}" class="card-product__media card-product__media--paper" style="display: flex;">
        <img src="${it.image}" alt="${escapeHtml(it.title)}" loading="lazy">
      </a>
      <div class="card-product__info">
        <div class="card-product__meta">${escapeHtml(it.code || '')}</div>
        <div class="card-product__title">${escapeHtml(it.title)}</div>
        <div class="card-product__price">${formatMoney(it.price)}</div>
        <button type="button" class="mono" style="color: var(--fg-mute); margin-top: var(--s-2); text-align: left;" data-wishlist-remove="${it.handle}">REMOVE</button>
      </div>
    </div>`;
  }
  function syncToggleStates() {
    const handles = new Set(readWishlist().map((x) => x.handle));
    document.querySelectorAll('[data-wishlist-toggle]').forEach((btn) => {
      const h = btn.dataset.productHandle;
      const saved = handles.has(h);
      btn.classList.toggle('is-saved', saved);
      const label = btn.querySelector('[data-wishlist-label]');
      if (label) label.textContent = saved ? 'SAVED' : 'SAVE';
    });
  }
  function initWishlist() {
    bindDrawer('[data-wishlist-drawer]', '[data-wishlist-open]', '[data-wishlist-close]', '[data-wishlist-overlay]', renderWishlist);
    document.querySelectorAll('[data-wishlist-toggle]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const handle = btn.dataset.productHandle;
        const list = readWishlist();
        const exists = list.findIndex((x) => x.handle === handle);
        if (exists >= 0) {
          list.splice(exists, 1);
        } else {
          list.push({
            handle,
            title: btn.dataset.productTitle,
            price: parseInt(btn.dataset.productPrice, 10),
            image: btn.dataset.productImage,
            code: btn.dataset.productCode,
          });
        }
        writeWishlist(list);
        renderWishlist();
        syncToggleStates();
      });
    });
    renderWishlist();
    syncToggleStates();
  }

  // — PDP variant state: price + stock per selected size
  function initVariantState() {
    const radios = document.querySelectorAll('.pdp__sizes input[name="id"]');
    if (!radios.length) return;
    const status = document.querySelector('[data-stock-status]');
    const stickyPrice = document.querySelector('[data-sticky-price]');
    const stickyStock = document.querySelector('[data-sticky-stock]');
    const atcBtn = document.querySelector('[data-atc-btn]');
    const hint = document.querySelector('[data-size-hint]');
    const LOW = 10; // "RUNNING LOW" tier — all from REAL inventory; tune to taste
    function update() {
      const sel = document.querySelector('.pdp__sizes input[name="id"]:checked');
      if (!sel) return;
      const size = sel.dataset.size || '';
      const price = parseInt(sel.dataset.price, 10);
      const inv = parseInt(sel.dataset.inventory, 10);
      const managed = sel.dataset.managed === '1';
      const available = sel.dataset.available === '1';
      const last = available && managed && inv > 0 && inv <= 3;
      const lowish = available && managed && inv > 3 && inv <= LOW;
      const urgent = last || lowish;
      const stockLabel = !available ? 'SOLD OUT' : last ? `● LAST ${inv}` : lowish ? '● RUNNING LOW' : '● IN STOCK';
      if (status) {
        status.innerHTML = available
          ? `<span${urgent ? ' style="color: var(--urgency);"' : ''}>${stockLabel}</span>`
          : 'SOLD OUT';
      }
      if (hint) {
        if (last) { hint.hidden = false; hint.innerHTML = `<span style="color: var(--urgency);">● ONLY ${inv} LEFT IN ${size}</span>`; }
        else if (lowish) { hint.hidden = false; hint.innerHTML = `<span style="color: var(--urgency);">● ${size} IS RUNNING LOW</span>`; }
        else { hint.hidden = true; hint.textContent = ''; }
      }
      if (stickyPrice && price) stickyPrice.textContent = formatMoney(price);
      if (stickyStock) stickyStock.textContent = available ? stockLabel : '';
      if (atcBtn && available && price) atcBtn.textContent = `ADD TO BAG — ${formatMoney(price)} →`;
    }
    update(); // reflect the initially-selected size's urgency on load
    radios.forEach((r) => r.addEventListener('change', update));
  }

  // — Notify-me (back-in-stock) panel: opened by clicking a sold-out size
  function initNotify() {
    const panel = document.querySelector('[data-notify-panel]');
    if (!panel) return;
    const label = panel.querySelector('[data-notify-label]');
    const tags = panel.querySelector('[data-notify-tags]');
    const body = panel.querySelector('[data-notify-body]');
    document.querySelectorAll('[data-notify-size]').forEach((el) => {
      el.addEventListener('click', (e) => {
        e.preventDefault();
        const size = el.dataset.notifySize;
        panel.hidden = false;
        if (label) label.textContent = `NOTIFY ME — ${size} RESTOCK`;
        if (tags) tags.value = `${tags.dataset.baseTags},size-${size}`;
        if (body) body.value = `${body.dataset.baseBody} — size ${size}`;
        panel.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const input = panel.querySelector('input[type="email"]');
        if (input) input.focus({ preventScroll: true });
      });
    });
    // Shopify contact form redirects back with ?contact_posted=true
    if (window.location.search.includes('contact_posted=true')) {
      panel.hidden = false;
      const form = panel.querySelector('form');
      if (form) form.setAttribute('hidden', '');
      const ok = panel.querySelector('[data-notify-success]');
      if (ok) ok.removeAttribute('hidden');
    }
  }

  // — Tier pill (server-rendered customer tier preferred, localStorage stub fallback)
  function initTierPill() {
    const serverPill = document.querySelector('[data-customer-tier]');
    const tier = (serverPill && serverPill.dataset.customerTier) || localStorage.getItem('hvnmde:tier') || 'guest';
    const map = {
      guest: { label: 'GUEST', cls: 'tier-pill--guest', show: true },
      list: { label: 'LIST', cls: 'tier-pill--list', show: true },
      regulars: { label: 'REGULARS', cls: 'tier-pill--regulars', show: true },
      'friends-family': { label: 'F&F', cls: 'tier-pill--ff', show: true },
    };
    const cfg = map[tier] || map.guest;
    document.querySelectorAll('[data-tier-pill]').forEach((el) => {
      el.textContent = cfg.label;
      el.className = 'tier-pill ' + cfg.cls + (el.classList.contains('site-header__pill') ? ' site-header__pill' : '');
      if (cfg.show) el.removeAttribute('hidden');
    });
  }

  // — Cookie banner
  function initCookieBanner() {
    const banner = document.querySelector('[data-cookie-banner]');
    if (!banner) return;
    const stored = localStorage.getItem('hvnmde:cookies');
    if (!stored) {
      setTimeout(() => banner.setAttribute('aria-hidden', 'false'), 600);
    }
    // forward consent to Shopify's Customer Privacy API so the native banner
    // (admin: disable it) and app pixels share ONE consent source
    function syncShopifyConsent(analytics, marketing) {
      try {
        window.Shopify?.customerPrivacy?.setTrackingConsent(
          { analytics: !!analytics, marketing: !!marketing, preferences: true, sale_of_data: !!marketing },
          () => {}
        );
      } catch (e) { /* API not loaded — localStorage remains source of truth */ }
    }
    banner.querySelector('[data-cookie-accept]')?.addEventListener('click', () => {
      localStorage.setItem('hvnmde:cookies', JSON.stringify({ ts: Date.now(), choice: 'all', analytics: true, marketing: true }));
      syncShopifyConsent(true, true);
      banner.setAttribute('aria-hidden', 'true');
    });
    const detail = banner.querySelector('[data-cookie-detail]');
    banner.querySelector('[data-cookie-customize]')?.addEventListener('click', () => {
      if (detail) detail.hidden = !detail.hidden;
    });
    banner.querySelector('[data-cookie-save]')?.addEventListener('click', () => {
      const prefs = {};
      banner.querySelectorAll('[data-cookie-key]').forEach((cb) => { prefs[cb.dataset.cookieKey] = cb.checked; });
      localStorage.setItem('hvnmde:cookies', JSON.stringify({ ts: Date.now(), choice: 'custom', ...prefs }));
      syncShopifyConsent(prefs.analytics, prefs.marketing);
      banner.setAttribute('aria-hidden', 'true');
    });
  }

  // — Mobile menu
  function initMobileMenu() {
    bindDrawer('[data-mobile-menu]', '[data-mobile-menu-open]', '[data-mobile-menu-close]', '[data-mobile-menu-overlay]');
    const burger = document.querySelector('.hamburger');
    const menu = document.querySelector('[data-mobile-menu]');
    if (burger && menu) {
      const obs = new MutationObserver(() => {
        burger.classList.toggle('is-open', menu.getAttribute('aria-hidden') === 'false');
      });
      obs.observe(menu, { attributes: true, attributeFilter: ['aria-hidden'] });
    }
  }

  // — Recently viewed
  const RECENT_KEY = 'hvnmde:recent';
  function trackPDPVisit() {
    const titleEl = document.querySelector('.pdp__title');
    const codeEl = document.querySelector('.pdp__code');
    const priceEl = document.querySelector('.pdp__price');
    const firstImg = document.querySelector('.pdp__media-stack img, .pdp__media-stack video');
    if (!titleEl) return;
    const handle = window.location.pathname.replace('/products/', '').replace(/\/$/, '');
    const entry = {
      handle,
      title: titleEl.textContent.trim(),
      code: codeEl?.textContent.trim() || '',
      price: parsePriceCents(priceEl?.textContent || '0'),
      image: firstImg ? (firstImg.tagName === 'IMG' ? firstImg.src : firstImg.poster) : '',
    };
    let list = [];
    try { list = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]'); } catch {}
    list = [entry, ...list.filter((x) => x.handle !== handle)].slice(0, 10);
    localStorage.setItem(RECENT_KEY, JSON.stringify(list));
  }
  function parsePriceCents(s) {
    const m = String(s).replace(/[^0-9.,]/g, '').replace(',', '.');
    const n = parseFloat(m);
    return isNaN(n) ? 0 : Math.round(n * 100);
  }
  function initRecentlyViewed() {
    if (window.location.pathname.startsWith('/products/')) trackPDPVisit();
    const section = document.querySelector('[data-recently-viewed]');
    if (!section) return;
    let list = [];
    try { list = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]'); } catch {}
    const currentHandle = window.location.pathname.replace('/products/', '').replace(/\/$/, '');
    list = list.filter((x) => x.handle !== currentHandle).slice(0, 4);
    if (list.length < 1) return;
    const grid = section.querySelector('[data-recently-grid]');
    if (grid) {
      grid.innerHTML = list.map((it) => `
        <a href="/products/${it.handle}" class="card-product">
          <div class="card-product__media card-product__media--paper">
            <img src="${it.image}" alt="${escapeHtml(it.title)}" loading="lazy">
          </div>
          <div class="card-product__info">
            <div class="card-product__meta">${escapeHtml(it.code || '')}</div>
            <div class="card-product__title">${escapeHtml(it.title)}</div>
            <div class="card-product__price">${formatMoney(it.price)}</div>
          </div>
        </a>`).join('');
    }
    section.removeAttribute('hidden');
  }

  // — Header scroll state (transparent → solid)
  function initHeaderScroll() {
    const header = document.querySelector('.site-header');
    if (!header) return;
    // Pages with a hero: light header over the scrim, morph to solid+mark partway down.
    // Mobile PDP gets the same treatment over its media fold (desktop PDP stays solid).
    // Pages without either have a light surface at top — solid from the very first frame.
    const pdpFold = window.matchMedia('(max-width: 960px)').matches
      ? document.querySelector('[data-overlay-fold]') : null;
    const fold = document.querySelector('.hero') || pdpFold;
    const threshold = fold ? Math.max(80, fold.offsetHeight * 0.5) : -1;
    function update() {
      const on = window.scrollY > threshold;
      header.classList.toggle('is-scrolled', on);
      document.body.classList.toggle('is-scrolled', window.scrollY > 40); // hero scroll-cue fade
    }
    update();
    window.addEventListener('scroll', update, { passive: true });
  }

  // — PDP sticky bottom ATC (mobile, scroll-triggered)
  function initStickyAtc() {
    const sticky = document.querySelector('[data-sticky-atc]');
    const trigger = document.querySelector('[data-sticky-atc-trigger]');
    const form = document.querySelector('form[action="/cart/add"], form[action^="/cart/add"]');
    if (!sticky || !trigger || !form) return;
    const mainBtn = form.querySelector('button[type="submit"]');

    sticky.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('has-sticky-atc');

    if (mainBtn) {
      let raf = 0;
      function update() {
        const r = mainBtn.getBoundingClientRect();
        // Show sticky as soon as the main ATC button has scrolled past the upper portion
        // of the viewport — bottom edge crosses above 60px from viewport top
        const passedAtc = r.bottom < 60;
        sticky.setAttribute('aria-hidden', passedAtc ? 'false' : 'true');
        document.body.classList.toggle('has-sticky-atc', passedAtc);
        raf = 0;
      }
      function onScroll() {
        if (raf) return;
        raf = requestAnimationFrame(update);
      }
      update();
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', update, { passive: true });
    }

    trigger.addEventListener('click', () => {
      const sizeFieldset = form.querySelector('.pdp__sizes');
      const selectedSize = form.querySelector('input[name="id"]:checked');
      if (sizeFieldset && !selectedSize) {
        sizeFieldset.scrollIntoView({ behavior: 'smooth', block: 'center' });
        sizeFieldset.animate(
          [{ outline: '2px solid var(--fg)', outlineOffset: '4px' }, { outline: '0 solid transparent', outlineOffset: '0' }],
          { duration: 1200, easing: 'ease-out' }
        );
        return;
      }
      form.requestSubmit ? form.requestSubmit() : form.submit();
    });
  }

  // — PDP mobile carousel: dots + counter + scroll-sync
  function initMediaCarousel() {
    const stack = document.querySelector('[data-media-stack]');
    const dotsHost = document.querySelector('[data-media-dots]');
    const current = document.querySelector('[data-media-current]');
    const total = document.querySelector('[data-media-total]');
    if (!stack || !dotsHost) return;
    const items = stack.querySelectorAll('.media');
    if (!items.length) return;
    if (total) total.textContent = items.length;

    // Build dots
    dotsHost.innerHTML = '';
    items.forEach((_, i) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'dot' + (i === 0 ? ' is-active' : '');
      b.setAttribute('role', 'tab');
      b.setAttribute('aria-label', `Slide ${i + 1}`);
      b.addEventListener('click', () => {
        items[i].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
      });
      dotsHost.appendChild(b);
    });

    const dots = dotsHost.querySelectorAll('.dot');
    function updateActive() {
      const w = stack.clientWidth;
      const idx = Math.round(stack.scrollLeft / w);
      const clamped = Math.max(0, Math.min(items.length - 1, idx));
      dots.forEach((d, i) => d.classList.toggle('is-active', i === clamped));
      if (current) current.textContent = clamped + 1;
    }
    stack.addEventListener('scroll', () => requestAnimationFrame(updateActive), { passive: true });

    // "VIEW ALL" → expand vertical stack below carousel (fallback to desktop pattern)
    document.querySelector('[data-media-fullscreen]')?.addEventListener('click', () => {
      stack.classList.toggle('is-expanded-vertical');
      stack.style.cssText = stack.classList.contains('is-expanded-vertical')
        ? 'display: grid !important; grid-template-rows: auto; height: auto !important; overflow: visible !important; gap: var(--s-3);'
        : '';
    });
  }

  // — PDP media expand toggle
  function initMediaToggle() {
    const stack = document.querySelector('[data-media-stack]');
    const btn = document.querySelector('[data-media-toggle]');
    if (!stack || !btn) return;
    const count = stack.querySelectorAll('.media').length;
    if (count <= 1) { btn.style.display = 'none'; return; }
    const countEl = btn.querySelector('[data-media-count]');
    if (countEl) countEl.textContent = `(${count})`;
    btn.addEventListener('click', () => {
      const expanded = stack.classList.toggle('is-expanded');
      btn.setAttribute('aria-expanded', String(expanded));
      btn.firstChild.textContent = expanded ? 'HIDE MEDIA ' : 'SHOW ALL MEDIA ';
    });
  }

  // — Boot
  function boot() {
    initHeaderScroll();
    initMediaToggle();
    initStickyAtc();
    initMediaCarousel();
    initImageLoader();
    initLenis();
    initSplitLines();
    initReveals();
    initAccordion();
    initFitFinder();
    initVariantState();
    initNotify();
    bindDrawer('[data-sg-drawer]', '[data-sg-open]', '[data-sg-close]', '[data-sg-overlay]');
    initCartDrawer();
    initClock();
    initCountdown();
    initWishlist();
    initTierPill();
    initCookieBanner();
    initMobileMenu();
    initRecentlyViewed();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
